import React from 'react';

import { useScrollIntoViewOnChange } from 'hooks/common/useScrollIntoViewOnChange';
import MergeClassNames from 'utils/MergeClassNames';

import OptionMetaRow from './OptionMetaRow';
import { OPTION_ROW_BASE_CLASSNAME, StepSelectorOption } from './stepConfig';

interface OptionRowProps {
  label: string;
  metaOption: StepSelectorOption;
  isHighlighted: boolean;
  isFilled?: boolean;
  /** Multi-select keyboard cursor: an inset ring that shows even over a checked (filled) row. */
  showHighlightRing?: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  /** Row marker attributes, e.g. { [PICKER_OPTION_ID_ATTRIBUTE]: id }. */
  navAttributes?: Record<string, string>;
}

/**
 * Picker row: label + OptionMetaRow, with optional leading / trailing slots (checkbox, caret, chip).
 * Used by the plain list, Seed / Seed Treatment / variety lists and the edit two-panels;
 * scrolls itself into view when keyboard navigation highlights it.
 */
const OptionRow: React.FC<OptionRowProps> = ({
  label,
  metaOption,
  isHighlighted,
  isFilled = isHighlighted,
  showHighlightRing = false,
  onClick,
  leading,
  trailing,
  navAttributes,
}) => {
  const rowRef = useScrollIntoViewOnChange<HTMLButtonElement>(isHighlighted);

  return (
    <button
      type="button"
      ref={rowRef}
      {...navAttributes}
      className={MergeClassNames(
        OPTION_ROW_BASE_CLASSNAME,
        isFilled ? 'bg-success-light' : 'bg-white',
        showHighlightRing && 'ring-2 ring-inset ring-charcoal'
      )}
      onClick={onClick}
    >
      {leading}
      <div className="flex-1">
        <div
          className={MergeClassNames(
            'text-base leading-5 text-charcoal',
            (isFilled || showHighlightRing) && 'font-semibold'
          )}
        >
          {label}
        </div>
        <OptionMetaRow option={metaOption} />
      </div>
      {trailing}
    </button>
  );
};

export default OptionRow;
